import { Rarity, Tag, type ModDef } from '../../sim/mods';
import { Stat } from '../../sim/stats';

/* ═══════════════════════════════════════════════════════════════════════════
   La voie du VERRE — neuvième famille du pool (J12).

   Toutes ces cartes vendent une partie de la marge de survie contre de la
   puissance immédiate. C'est la seule famille dont on peut dire qu'une run
   entière peut se construire dessus, et qu'une autre run peut légitimement la
   refuser en bloc — ce qui est la définition d'une voie.

   ⚠️ Elles frôlent l'interdit du plan (§6.7 : « aucun mod n'est jamais un piège
   mort, pas de malus pur non retirable »). La ligne de partage tenue ici :

     · le coût est TOUJOURS payé sur une statistique absente de la formule de
       DPS. Une carte ne peut donc jamais faire baisser la puissance de feu,
       même quand un plafond mord — c'est le piège exact que `STAT_CAPS`
       documente ;
     · le coût est PLAFONNÉ par `maxStacks`. « −1 vie » est un pari ; « −4 vies »
       serait une carte qu'un joueur mal informé ne peut plus refuser d'avoir
       prise ;
     · le gain est franchement supérieur au coût. Ces cartes doivent tenter,
       pas punir.
   ═══════════════════════════════════════════════════════════════════════════ */

const mods: ModDef[] = [
  {
    id: 'coque-allegee',
    name: 'Coque allégée',
    rarity: Rarity.Rare,
    tags: [Tag.Surcharge],
    // Un exemplaire, et c'est la contrainte la plus importante du fichier :
    // trois vies est le capital de départ, en vendre une reste un pari qu'on
    // peut assumer. Autoriser deux exemplaires ferait exister un build à une
    // vie, où la moindre erreur annule vingt minutes de jeu.
    maxStacks: 1,
    modifiers: [
      { stat: Stat.Damage, op: 'mul', value: 0.52 },
      { stat: Stat.MaxLives, op: 'add', value: -1 },
    ],
    text: '+52 % de dégâts, −1 vie',
  },
  {
    id: 'soutes-vidangees',
    name: 'Soutes vidangées',
    rarity: Rarity.Rare,
    tags: [Tag.Surcharge, Tag.Thermique],
    // Deuxième carte qui vend une bombe, et il fallait qu'elle ne soit pas la
    // même que « Injection forcée » : deux cartes au même prix et au même effet
    // ne posent qu'une question, celle de savoir laquelle est tombée. Celle-ci
    // allège le vaisseau — mobilité d'abord, cadence ensuite ; l'autre pousse la
    // cadence seule. Prendre les deux vide la réserve, ce que le joueur voit
    // venir sur la carte.
    maxStacks: 1,
    modifiers: [
      { stat: Stat.MoveSpeed, op: 'mul', value: 0.2 },
      { stat: Stat.FireRate, op: 'mul', value: 0.18 },
      { stat: Stat.MaxBombs, op: 'add', value: -1 },
    ],
    text: '+20 % de vitesse de déplacement, +18 % de cadence, −1 bombe',
  },
  {
    id: 'pilote-temeraire',
    name: 'Pilote téméraire',
    rarity: Rarity.Rare,
    tags: [Tag.Surcharge, Tag.Orbital],
    maxStacks: 2,
    // Le coût est en SCORE, pas en survie : le frôlement rétréci ne tue pas, il
    // appauvrit. C'est la version douce de la voie, celle qu'on peut prendre
    // sans avoir décidé de tout miser.
    modifiers: [
      { stat: Stat.Damage, op: 'mul', value: 0.16 },
      { stat: Stat.FireRate, op: 'mul', value: 0.12 },
      { stat: Stat.GrazeRadius, op: 'mul', value: -0.3 },
    ],
    text: '+16 % de dégâts, +12 % de cadence, −30 % de rayon de frôlement',
  },
  {
    id: 'convertisseur',
    name: 'Convertisseur de charge',
    rarity: Rarity.Rare,
    tags: [Tag.Surcharge, Tag.Drone],
    maxStacks: 2,
    // La seule carte qui échange explicitement de la MÉTA contre de la run : les
    // cellules financent l'alliage, donc la progression entre les parties. Un
    // joueur qui vise le score la refuse, un joueur qui vise le boss la prend.
    modifiers: [
      { stat: Stat.Damage, op: 'mul', value: 0.28 },
      { stat: Stat.CellGain, op: 'mul', value: -0.3 },
    ],
    text: '+28 % de dégâts, −30 % de cellules',
  },
  {
    id: 'lest-largue',
    name: 'Lest largué',
    rarity: Rarity.Common,
    tags: [Tag.Surcharge],
    maxStacks: 2,
    // La carte de la voie qui achète surtout de l'ESQUIVE — la ressource que le
    // banc d'équilibrage ne sait pas mesurer et qui décide pourtant des runs.
    // Les +6 % de dégâts ne sont pas de la générosité : le banc a montré que le
    // décile de parties le plus pauvre est celui qui a enchaîné les cartes sans
    // puissance de feu. Un filet de dégâts sur les cartes utilitaires resserre
    // l'écart entre une bonne et une mauvaise pioche sans rien retirer au choix.
    modifiers: [
      { stat: Stat.MoveSpeed, op: 'mul', value: 0.26 },
      { stat: Stat.Damage, op: 'mul', value: 0.06 },
      { stat: Stat.BulletRadius, op: 'mul', value: -0.15 },
    ],
    text: '+26 % de vitesse de déplacement, +6 % de dégâts, −15 % de largeur de tir',
  },
];

export default mods;
